<template>
  <div v-click-outside="e => handleClickOutside(e)" class="task-container">
    <div v-if="isXSmallScreenAndDown" class="backdrop" @click="() => $emit('close')"></div>
    <div class="task-content">
      <div class="task-content-header">
        <div>
          <FriedH3 class="map-title paragraph-large">
            {{ task.title }}
          </FriedH3>
          <InlineRichTextEditorViewer
            :content="formattedDescription"
            data-external="map-description-text"
          />
        </div>
        <FriedIconButton data-external="close-task-drawer" @click="$emit('close')">
          <FriedIcon :size="IconSize.Small" icon="close" />
        </FriedIconButton>
      </div>
      <div class="task-detail-container">
        <div class="task-details">
          <FriedH4>{{ t('MAP_status') }}</FriedH4>
          <EditTaskStatus
            v-if="isAssignee"
            :status="task.status"
            @set-status="task.status = $event"
          />
          <FriedStatusBadge
            v-else-if="task.status === TaskStatus.Incomplete"
            :text="t('MAP_not_completed')"
            :status-type="StatusType.Error"
          />
          <FriedStatusBadge v-else :status-type="StatusType.Success" :text="t('map_completed')" />
          <span class="divider" />
          <FriedH4>{{ t('MAP_due_date') }}</FriedH4>
          <div class="deadline-container">
            <template v-if="deadline"> <FriedIcon icon="calendar" />{{ deadline }}</template>
          </div>
          <span class="divider" />
          <FriedH4>{{ t('map_task_owners') }}</FriedH4>
          <FriedAvatarGroup
            v-if="task.participantIds.length > 0"
            class="task-owners"
            :avatars="getParticipantAvatars(selectableParticipants, task.participantIds)"
            :size="AvatarSize.Small"
            :visible-avatars="10"
          />
        </div>
      </div>
      <div
        v-if="isAssignee"
        :class="[
          'task-button-container',
          {
            'justify-end': participantId,
          },
        ]"
      >
        <div class="action-buttons">
          <FriedButton
            :button-type="ButtonType.Secondary"
            data-external="task-drawer-button-cancel"
            @click="$emit('close')"
            >{{ t('cancel') }}</FriedButton
          >
          <FriedButton
            data-external="task-drawer-button-save"
            :disabled="!task.title"
            @click="handleSaveChanges"
          >
            {{ t('MAP_save_changes') }}
          </FriedButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { AvatarSize, ButtonType, IconSize, ParagraphSize, StatusType } from '@getaccept/fried-tofu';
import { useI18n } from 'vue-i18n';
import type { Participant } from '@getaccept/dsr-shared-new';
import type { Task } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/action-plan';
import EditTaskStatus from '@getaccept/dsr-shared-new/src/mutual-action-plan/components/EditTaskStatus.vue';
import { TaskStatus } from '@getaccept/dsr-shared-new/src/mutual-action-plan/types/enums/task-status';
import { MilestoneHelper } from '@getaccept/dsr-shared-new/src/mutual-action-plan/helpers/milestone.helper';
import { TaskHelper } from '@getaccept/dsr-shared-new/src/mutual-action-plan/helpers/task.helper';
import { useEditTask } from '@getaccept/dsr-shared-new/src/mutual-action-plan/composables/edit-task.composable';
import InlineRichTextEditorViewer from '@getaccept/dsr-shared-new/src/inline-text-editor/components/InlineRichTextEditorViewer.vue';

export default defineComponent({
  components: {
    EditTaskStatus,
    InlineRichTextEditorViewer,
  },
  props: {
    taskToEdit: {
      type: Object as PropType<Task>,
      required: true,
    },
    participants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    participantId: {
      type: String,
      default: '',
    },
    owner: {
      type: Object as PropType<Participant>,
      default: () => ({} as Participant),
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const {
      task,
      isCompleted,
      deadline,
      isXSmallScreenAndDown,
      selectableParticipants,
      formattedDescription,
      handleClickOutside,
    } = useEditTask(props, emit);

    const isAssignee = computed(() =>
      props.taskToEdit.participantIds.includes(props.participantId)
    );

    const handleSaveChanges = () => {
      task.value.status === props.taskToEdit.status
        ? emit('close')
        : emit('update-task-status', { taskId: task.value.id, status: task.value.status });
    };

    return {
      isAssignee,
      isCompleted,
      deadline,

      getProgress: MilestoneHelper.getProgress,
      getParticipantAvatars: TaskHelper.getParticipantAvatars,
      handleSaveChanges,

      ParagraphSize,
      ButtonType,
      TaskStatus,
      StatusType,
      formattedDescription,
      AvatarSize,
      IconSize,
      t,
      task,
      isXSmallScreenAndDown,
      handleClickOutside,
      selectableParticipants,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/components/scss/dsr-variables';
@import '@getaccept/dsr-shared-new/src/scss/edit-task';
</style>
